import Store from 'store/store';
import formatDateTime from 'utils/formatDateTime';
import getCopyWithParams from 'utils/getCopyWithParams';
import { parseDate } from 'utils/utils';

function getToday() {
	const now = new Date();
	return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function getReleaseMessagingCopy(movie, status = movie?.status, copy = Store.get().appData?.copy?.baseCopy) {
	if (movie?.releaseMessagingOverride) {
		return movie.releaseMessagingOverride;
	}

	if (!movie || !status) return '';

	const releaseMessaging = movie.releaseMessaging;

	if (status.theaters === 'inTheaters' && status.homeEnt === 'homeEnt') {
		return releaseMessaging?.inTheatersHomeEnt || copy?.['$RELEASE_THEATERS_HOMEENT'] || 'In Theaters and At Home';
	}

	if (status.theaters === 'inTheaters') {
		return releaseMessaging?.inTheaters || copy?.['$RELEASE_THEATERS'] || 'Now Playing In Theaters';
	}

	if (status.homeEnt === 'homeEnt') {
		return releaseMessaging?.homeEnt || copy?.['$RELEASE_HOMEENT'] || 'Watch At Home';
	}

	if (status.theaters === 'comingSoon' || status.theaters === 'preSale') {
		const releaseDateStr = movie.dates?.release;

		if (!releaseDateStr) {
			return releaseMessaging?.comingSoon || copy?.['$RELEASE_COMING_SOON'] || 'Coming Soon';
		}

		if (isReleaseWeek(releaseDateStr)) {
			return releaseMessaging?.comingSoonWeekOfRelease || copy?.['$RELEASE_WEEK'] || 'In Theaters %DAY%';
		}

		return releaseMessaging?.comingSoon || copy?.['$RELEASE_FUTURE'] || 'In Theaters %DATE%';
	}

	return '';
}

export function isReleaseWeek(dateStr, date = parseDate(dateStr)) {
	const today = getToday();

	// Only check for fully qualified dates
	if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false;
	if (!date || today >= date) return false;

	const mondayBeforeRelease = new Date(+date);
	mondayBeforeRelease.setDate(mondayBeforeRelease.getDate() - (mondayBeforeRelease.getDay() + 6) % 7);
	return today >= mondayBeforeRelease;
}

export function getReleaseDateFormat(movie, copy = Store.get().appData?.copy?.baseCopy) {
	const releaseDateStr = movie.dates?.release;
	if (!releaseDateStr) return '';

	const releaseDate = parseDate(releaseDateStr);
	const now = new Date();
	const sameYear = now.getFullYear() === releaseDate.getFullYear();

	let dateFormat = copy?.['$RELEASE_DATE_FORMAT_COMPACT'] || 'MMMM d';
	if (/^\d{4}$/.test(movie.dates.release)) {
		dateFormat = copy?.['$RELEASE_DATE_FORMAT_YEAR_ONLY'] || 'yyyy';
	} else if (/^\d{4}-\d{2}$/.test(movie.dates.release)) {
		if (sameYear) {
			dateFormat = copy?.['$RELEASE_DATE_FORMAT_MONTH_ONLY_COMPACT'] || 'MMMM';
		} else {
			dateFormat = copy?.['$RELEASE_DATE_FORMAT_MONTH_ONLY'] || 'MMMM yyyy';
		}
	} else if (!sameYear) {
		dateFormat = copy?.['$RELEASE_DATE_FORMAT'] || 'MMMM d, yyyy';
	}

	return dateFormat;
}

export function getReleaseDay(movie, copy = Store.get().appData?.copy?.baseCopy) {
	const release = movie?.dates?.release;
	if (!release || !/^\d{4}-\d{2}-\d{2}$/.test(release)) return '';

	const releaseDate = parseDate(release);
	const dateFormat = copy?.['$RELEASE_DATE_FORMAT_DAY_OF_WEEK'] || 'dddd';
	return formatDateTime(releaseDate, dateFormat, copy?.['$DATETIME_LOCALE']);
}

export function getReleaseDate(movie, copy = Store.get().appData?.copy?.baseCopy) {
	const release = movie?.dates?.release;
	if (!release) return '';

	const releaseDate = parseDate(release);
	const dateFormat = getReleaseDateFormat(movie, copy);
	return formatDateTime(releaseDate, dateFormat, copy?.['$DATETIME_LOCALE']);
}

/**
 * Replaces %DATE% and %DAY% placeholders with the movie's formatted release date
 * and weekday, using localized date formats. Missing release dates produce empty values.
 *
 * @param {string} text - Text containing release date placeholders.
 * @param {Object} movie - Movie with a release date in dates.release.
 * @param {Object} [copy] - Date formats and locale; defaults to the store's base copy.
 * @returns {string} Text with release date placeholders resolved.
 */
export function formatReleaseMessaging(text, movie, copy = Store.get().appData?.copy?.baseCopy) {
	return getCopyWithParams(
		{
			DATE: getReleaseDate(movie, copy),
			DAY: getReleaseDay(movie, copy),
		},
		text
	);
}

export function getReleaseMessaging(movie, status = movie?.status, copy = Store.get().appData?.copy?.baseCopy) {
	const releaseMessagingCopy = getReleaseMessagingCopy(movie, status, copy);
	if (!releaseMessagingCopy) return '';

	return formatReleaseMessaging(releaseMessagingCopy, movie, copy);
}